import axios from 'axios';
import { apiRoute } from '../constants/api-constants';

/**
 * THE SUPER ADMIN CONSOLE'S API CLIENT.
 *
 * Uses the DEFAULT axios instance, like every other authenticated client in
 * this app, so it inherits the interceptors in axios-setup.ts: credentials on,
 * the CSRF header echoed from the readable cookie, and a 401 -> refresh ->
 * retry that happens once and is single-flighted.
 *
 * ── EVERY CALL IS RE-AUTHORISED SERVER-SIDE ────────────────────────────────
 *
 * Nothing in this file is a security control. `is_platform_admin` on the user
 * decides whether the console's NAVIGATION renders; the server asks
 * `is_platform_admin()` against the database on every single request and
 * answers 404 to anyone else. Hiding a link is a courtesy.
 *
 * ── 404 IS THE "YOU ARE NOT AN ADMIN" ANSWER ───────────────────────────────
 *
 * Not 403. A 403 on /api/admin/organizations would confirm to an ordinary
 * customer that a cross-tenant console exists at that address; a 404 says the
 * path is not there, which is what it means for them. The console's own error
 * handling therefore has to treat a 404 on a LIST endpoint as "your access was
 * withdrawn" rather than as "no such record".
 */

const ADMIN = `${apiRoute}/admin`;

/** A cursor-paginated page. `next` is null on the last page. */
export interface Page<T> {
  items: T[];
  next: string | null;
  total?: number;
}

export interface AdminSession {
  user_id: string;
  label: string;
  /** Seconds of step-up left. 0 = a sensitive action will be refused. */
  elevated_for_s: number;
}

export class ElevationRequiredError extends Error {
  constructor() {
    super('Re-authentication required');
    this.name = 'ElevationRequiredError';
  }
}

export class AdminAccessError extends Error {
  constructor() {
    super('Your platform administrator access is not active.');
    this.name = 'AdminAccessError';
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const unwrap = (e: any): never => {
  const status = e?.response?.status;
  const code = e?.response?.data?.code;
  if (code === 'ELEVATION_REQUIRED') throw new ElevationRequiredError();
  if (status === 404 && !e?.config?.url?.match(/\/[^/]+$/)) throw new AdminAccessError();
  throw e;
};

const get = async <T>(path: string, params?: Record<string, unknown>): Promise<T> => {
  try {
    const r = await axios.get(`${ADMIN}${path}`, { params });

    return r.data as T;
  } catch (e) {
    return unwrap(e);
  }
};

const post = async <T>(path: string, body?: Record<string, unknown>): Promise<T> => {
  try {
    const r = await axios.post(`${ADMIN}${path}`, body ?? {});

    return r.data as T;
  } catch (e) {
    return unwrap(e);
  }
};

const patch = async <T>(path: string, body?: Record<string, unknown>): Promise<T> => {
  try {
    const r = await axios.patch(`${ADMIN}${path}`, body ?? {});

    return r.data as T;
  } catch (e) {
    return unwrap(e);
  }
};

const del = async <T>(path: string, body?: Record<string, unknown>): Promise<T> => {
  try {
    const r = await axios.delete(`${ADMIN}${path}`, { data: body ?? {} });

    return r.data as T;
  } catch (e) {
    return unwrap(e);
  }
};

/* ── session / step-up ─────────────────────────────────────────────────── */

export const getSession = () => get<AdminSession>('/session');

/* ── the alarm on every page (src/ops/mail-health.js on the server) ───── */

export interface AdminAlert {
  level: 'critical' | 'warning';
  key: string;
  title: string;
  detail: string;
  action: string;
  since?: string | null;
  checked_at?: string | null;
}
export const getAlerts = (recheck = false) =>
  get<{ alerts: AdminAlert[]; checked_at: string }>(recheck ? '/alerts?recheck=1' : '/alerts');
export const elevate = (password: string) =>
  post<{ elevated_for_s: number }>('/session/elevate', { password });
export const stepDown = () => post<{ elevated_for_s: number }>('/session/step-down');

/* ── the dashboard ─────────────────────────────────────────────────────── */

export interface Overview {
  counts: Record<string, string | number>;
  subscriptions: { status: string; n: number; units: number }[];
  revenue: { mrr_cents_mirror: number; authority: string; note: string };
  /** `month` is the 'YYYY-MM' the month-to-date figures cover, on the READER's
   *  calendar (`tz`) — the first hours of a month used to be Greenwich's. */
  iq: { spend_micros_mtd: number; calls_mtd: number; errors_mtd: number; month?: string | null };
  warnings: Record<string, string | number>;
  over_allowance: { id: string; name: string; used: number; bought: number }[];
  sources: Record<string, string>;
  /** The console is on the reader's clock: `viewer`, or `fallback` (UTC) when
   *  the device's zone did not arrive. */
  tz?: string;
  tz_source?: 'viewer' | 'fallback';
}
export const getOverview = () => get<Overview>('/overview');

export interface SearchResult {
  type: string;
  id: string | number;
  label: string;
  sub?: string;
  org?: string;
  href: string;
}
export const search = (q: string) =>
  get<{ results: SearchResult[]; limits: string[] }>('/search', { q });

/* ── the lists ─────────────────────────────────────────────────────────── */

/**
 * ONE FUNCTION FOR EVERY LIST, and the console's table hook is written against
 * it. The alternative — a bespoke function per resource — is twenty near
 * identical wrappers whose only difference is a string, and the first one
 * somebody forgets to pass `cursor` to becomes a list that silently only ever
 * shows page one.
 */
export type ListParams = Record<string, string | number | undefined | null>;
export const list = <T>(resource: string, params: ListParams = {}) =>
  get<Page<T>>(`/${resource}`, params);

export const detail = <T>(path: string) => get<T>(path);

/* ── the box, the database, and every log line on the box ──────────────── */

/* eslint-disable @typescript-eslint/no-explicit-any */
export type OpsHealth = Record<string, any>;

export interface LogEntry {
  cursor: string;
  ts: string;
  ts_us: string;
  source: string;
  unit: string | null;
  ident: string | null;
  pid: number | null;
  level: number;
  level_name: string;
  tag: string | null;
  message: string;
  fields: Record<string, any> | null;
  detail: string[] | null;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface LogPage {
  /** `journal` on the box; `buffer` = this process's own memory, with `reason`. */
  backend: 'journal' | 'buffer';
  reason?: string | null;
  hint?: string | null;
  sources: { key: string; label: string }[];
  items: LogEntry[];
  scanned: number;
  exhausted: boolean;
  stopped_by: 'limit' | 'scan_cap' | 'time' | null;
  next_cursor: string | null;
  top_cursor: string | null;
}

export interface LogContext {
  backend: string;
  reason?: string;
  at: LogEntry | null;
  before: LogEntry[];
  after: LogEntry[];
}

/* Empty filters are dropped rather than sent as `status=` — the server treats
   them as "no filter" either way, but they would clutter every logged path. */
export const getLogs = (params: ListParams) =>
  get<LogPage>(
    '/ops/logs',
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))
  );
export const getLogContext = (cursor: string, sources: string) =>
  get<LogContext>('/ops/logs/context', { cursor, sources, n: 15 });

/* ── the sensitive actions ─────────────────────────────────────────────── */
/*
 * Each one takes a REASON, and it is required by the server (400
 * REASON_REQUIRED under eight characters). It is not decoration: it lands in
 * the platform audit trail AND in the customer's own audit_log, which is what
 * makes an action taken against somebody else's account answerable later.
 */

export const suspendOrg = (id: string, reason: string) =>
  post<{ id: string; status: string; previous: string }>(
    `/organizations/${encodeURIComponent(id)}/suspend`, { reason });
export const restoreOrg = (id: string, reason: string) =>
  post<{ id: string; status: string; previous: string }>(
    `/organizations/${encodeURIComponent(id)}/restore`, { reason });
// Lobbero Stays on/off for one organisation (plan S1; only while the platform
// policy flag_switch_owner is `console` — the route answers 403 otherwise).
export const setOrgStays = (id: string, enabled: boolean, reason: string) =>
  post<{ id: string; stays_enabled: boolean; previous: boolean }>(
    `/organizations/${encodeURIComponent(id)}/stays`, { enabled, reason });
// Independent Stays IS1: the organisation's products (Desk / Stays). A missing
// product keeps its value. 409 PRODUCTS_NEED_ONE | DESK_IS_A_PURCHASE |
// STAYS_OFF_HAS_FUTURE_STAYS | NO_CHANGE.
// IS6: comped = Desk on as a complimentary grant, refused for a customer who pays.
export const setOrgProducts = (id: string, products: { desk?: boolean; stays?: boolean; comped?: boolean }, reason: string) =>
  post<{ id: string; products: { desk: boolean; stays: boolean }; previous: { desk: boolean; stays: boolean } }>(
    `/organizations/${encodeURIComponent(id)}/products`, { ...products, reason });
// Independent Stays IS8.4: put a FAILED closed-account removal back in the ops
// timer's queue (elevated). 409 NO_CHANGE when it has not failed.
export const retryAccountClosure = (id: string, reason: string) =>
  post<{ id: string; status: string }>(`/stays/closures/${encodeURIComponent(id)}/retry`, { reason });
export const deactivateUser = (id: string, reason: string) =>
  post<{ id: string; status: string }>(`/users/${encodeURIComponent(id)}/deactivate`, { reason });
export const reactivateUser = (id: string, reason: string) =>
  post<{ id: string; status: string }>(`/users/${encodeURIComponent(id)}/reactivate`, { reason });
export const revokeSessions = (id: string, reason: string) =>
  post<{ id: string; revoked: number }>(
    `/users/${encodeURIComponent(id)}/revoke-sessions`, { reason });
/* ── PROMOTIONS ────────────────────────────────────────────────────────────
 *
 * Every field below is DERIVED BY THE SERVER. `status` is computed in SQL from
 * the window and the flags (a browser that worked it out from `expires_at`
 * would disagree with the filter on the very next screen), `description_public`
 * is the sentence a customer is shown, and the usage counts come from the
 * ledger. Nothing here is recomputed in the browser.
 */
export interface Promo {
  id: string;
  code: string;
  description: string | null;
  discount_percent: number;
  duration: 'forever' | 'once' | 'repeating';
  duration_in_months: number | null;
  applies_to_interval: 'month' | 'year' | 'both';
  plan_ids: number[] | null;
  customer_eligibility: 'all' | 'new' | 'existing';
  max_redemptions: number | null;
  max_per_customer: number | null;
  starts_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  archived_at: string | null;
  /** Server-computed: active | inactive | upcoming | expired | archived. */
  status: string;
  /** The sentence the customer sees, e.g. "20% off for 3 months". */
  description_public: string;
  redemptions_used?: number;
  redemptions_confirmed?: number;
  redemptions_total?: number;
  created_by_label: string;
  created_at: string;
  updated_by_label?: string | null;
  updated_at?: string;
  /* Shown to an operator so a promotion can be found in the Stripe dashboard.
     They are ids, not credentials, and this console is already the highest
     privilege surface in the product. */
  stripe_coupon_id: string;
  stripe_promotion_code_id: string;
}

export interface Redemption {
  id: string;
  promo_code_id?: string;
  code?: string;
  organization_id: string;
  organization_name: string | null;
  status: 'pending' | 'applied' | 'confirmed' | 'released';
  billing_interval: string | null;
  discount_percent: number | string;
  duration: string;
  duration_in_months: number | null;
  plan_code?: string | null;
  stripe_subscription_id: string | null;
  confirmed_invoice_id: string | null;
  confirmed_discount_cents: number | null;
  release_reason: string | null;
  created_at: string;
  applied_at: string | null;
  confirmed_at: string | null;
  released_at: string | null;
}

/** What a create or edit may carry. The server ignores anything else. */
export interface PromoInput {
  code?: string;
  description?: string | null;
  discount_percent?: number | string;
  duration?: string;
  /* Whole-number fields travel as the TEXT the operator typed. The server's
     validator accepts "20" and refuses "1,000", "one" and "2.5" by name;
     Number() in the browser turned those into NaN, which JSON serialises as
     null — i.e. "unlimited" — and a typo silently created an uncapped code. */
  duration_in_months?: number | string | null;
  applies_to_interval?: string;
  plan_ids?: number[] | null;
  customer_eligibility?: string;
  max_redemptions?: number | string | null;
  max_per_customer?: number | string | null;
  starts_at?: string | null;
  expires_at?: string | null;
}

export const getPromo = (id: string) =>
  get<{ promo: Promo; plans: { id: number; code: string; name: string }[]; redemptions: Redemption[] }>(
    `/promos/${encodeURIComponent(id)}`
  );
export interface PromoPlan {
  id: number;
  code: string;
  name: string;
  billing_interval: string;
  price_cents: number;
  /** Migration 176: 'desk' | 'stays'. Absent from an older API = desk. */
  product_line?: string;
}
/** The plans a promotion may be restricted to (purchasable, not legacy). */
export const getPromoPlans = () => get<{ items: PromoPlan[] }>('/promos/plans');
export const createPromo = (input: PromoInput, reason: string) =>
  post<Promo>('/promos', { ...input, reason });
export const updatePromo = (id: string, input: PromoInput, reason: string) =>
  patch<Promo>(`/promos/${encodeURIComponent(id)}`, { ...input, reason });
export const activatePromo = (id: string, reason: string) =>
  post<Promo>(`/promos/${encodeURIComponent(id)}/activate`, { reason });
export const deactivatePromo = (id: string, reason: string) =>
  post<Promo>(`/promos/${encodeURIComponent(id)}/deactivate`, { reason });
/* ARCHIVE IS THE ONLY "DELETE", AND IT IS ONE-WAY. There is no delete endpoint
   to call: the backend holds no DELETE privilege on either promo table, and a
   redemption that pointed at a promotion which no longer existed would be a
   billing record nobody could explain. */
export const archivePromo = (id: string, reason: string) =>
  post<Promo>(`/promos/${encodeURIComponent(id)}/archive`, { reason });

export const grantAdmin = (userId: string, reason: string) =>
  post<{ user_id: string }>('/admins', { user_id: userId, reason });
export const revokeAdmin = (userId: string, reason: string) =>
  del<{ user_id: string }>(`/admins/${encodeURIComponent(userId)}`, { reason });
